import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MonitorSmartphone, Clock, PanelRightOpen, PanelRightClose } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { MindPalEmbed } from "@/components/run/MindPalEmbed";
import { SessionContextChip } from "@/components/run/SessionContextChip";
import { GateStrip, FieldGuide } from "@/components/run/Guides";
import { FormAlert } from "@/components/common/Alert";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { modesFor } from "@/lib/catalogue";
import { ROLE_LABELS } from "@/lib/constants";
import { cn } from "@/lib/utils";

const REF_RE = /^(conversation|session|run|thread|chat)[_-]?id$/i;
function findRef(data, depth = 0) {
  if (depth > 4 || data == null) return null;
  if (typeof data === "string") {
    try { return findRef(JSON.parse(data), depth + 1); } catch { return null; }
  }
  if (typeof data !== "object") return null;
  for (const [k, v] of Object.entries(data)) {
    if (REF_RE.test(k) && (typeof v === "string" || typeof v === "number") && String(v)) return String(v).slice(0, 200);
    const nested = findRef(v, depth + 1);
    if (nested) return nested;
  }
  return null;
}

const ComingSoon = ({ team, text }) => (
  <div className="grid min-h-[320px] place-items-center rounded-xl border border-dashed bg-card/60 p-8 text-center" data-testid="run-surface-coming-soon">
    <div>
      <Clock className="mx-auto text-muted-foreground" aria-hidden="true" />
      <p className="mt-3 text-base font-semibold">Coming soon</p>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{text || `${team ? `${team.section || team.name} is building this one` : "This one is being built"}${team?.order ? `, number ${team.order} in the team build order` : ""}. Everything else on this page is ready to read.`}</p>
    </div>
  </div>
);

export const RunSurface = ({ kind, entity, gates = [], onRun, guideExtra, guideFields }) => {
  const [guideOpen, setGuideOpen] = useState(() => window.innerWidth >= 1440);
  const { user, campaign } = useAuth();
  const { teamsById } = useCatalogue();
  const [params] = useSearchParams();
  const reuseId = params.get("run");
  const modes = modesFor(entity, kind);
  const [mode, setMode] = useState(modes[0]);
  const [overrides, setOverrides] = useState({});
  const [run, setRun] = useState(null);
  const [error, setError] = useState("");
  const targetId = kind === "agent" ? entity.emp_no : entity.id;
  const slugFor = (m) => ((m === "chat" ? entity.mindpal?.chatbot_slug : entity.mindpal?.workflow_slug) || "").trim();
  const slug = slugFor(mode);
  const canRun = user.role !== "viewer" || entity.viewer_can_run;
  const ready = entity.live && canRun && !!slug;
  const context = {
    "user-name": user.name, company: "Fuse Capital", role: ROLE_LABELS[user.role], campaign: campaign || "none",
    "service-line": overrides.service_line || user.service_line || "none",
    "audience-stream": overrides.audience_stream || user.audience_stream || "none",
    region: overrides.region || user.region || "none",
  };
  const runRef = useRef(null);
  runRef.current = run;
  const nonceRef = useRef(null);
  if (!nonceRef.current) nonceRef.current = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

  useEffect(() => {
    const firstReady = modes.find((m) => slugFor(m));
    if (firstReady && !slugFor(mode)) setMode(firstReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  useEffect(() => {
    if (!entity.live || !canRun || !modes.some((m) => slugFor(m))) return;
    let cancelled = false;
    (async () => {
      try {
        let r = null;
        if (reuseId) {
          const { data } = await api.get(`/runs/${reuseId}`).catch(() => ({ data: null }));
          if (data && data.target_id === targetId) r = data;
        }
        if (!r) {
          const m = slugFor(mode) ? mode : modes.find((x) => slugFor(x));
          const { data } = await api.post("/runs", {
            target_type: kind, target_id: targetId, mode: m, campaign: campaign || "",
            service_line: user.service_line, audience_stream: user.audience_stream, region: user.region, nonce: `${targetId}-${nonceRef.current}`,
          });
          r = data;
        }
        if (!cancelled) { setRun(r); onRun?.(r); }
      } catch (e) {
        if (!cancelled) setError(errorMessage(e));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, reuseId]);

  const patch = useCallback(async (changes) => {
    const r = runRef.current;
    if (!r) return;
    try {
      const { data } = await api.patch(`/runs/${r.id}`, changes);
      setRun(data);
      onRun?.(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [onRun]);

  useEffect(() => {
    if (!run) return;
    patch({ campaign: campaign || "", service_line: context["service-line"] === "none" ? null : context["service-line"], audience_stream: context["audience-stream"] === "none" ? null : context["audience-stream"], region: context.region === "none" ? null : context.region });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, overrides]);

  useEffect(() => {
    const onMessage = (e) => {
      let host = "";
      try { host = new URL(e.origin).hostname; } catch { return; }
      if (!host.endsWith("getmindpal.com")) return;
      const ref = findRef(e.data);
      if (ref && runRef.current && !runRef.current.mindpal_ref) patch({ mindpal_ref: ref });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [patch]);

  const switchMode = (m) => { setMode(m); if (run && slugFor(m)) patch({ mode: m }); };
  const toggleGate = (g) => {
    if (!run) return;
    const ticks = run.gate_ticks.includes(g) ? run.gate_ticks.filter((x) => x !== g) : [...run.gate_ticks, g];
    patch({ gate_ticks: ticks });
  };

  const team = teamsById[entity.team];
  return (
    <section className="space-y-4" data-testid="run-surface-section" aria-label="Run surface">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="sr-only">Run it</h2>
        <div className="flex items-center gap-2">
          {modes.length > 1 && (
            <div className="flex rounded-xl bg-muted p-1" role="tablist" aria-label="Chat or form">
              {modes.map((m) => (
                <button key={m} role="tab" aria-selected={mode === m} onClick={() => switchMode(m)} data-testid={`run-mode-${m}`}
                  className={cn("rounded-lg px-3 py-1.5 text-sm transition-quick", mode === m ? "bg-card font-medium shadow-sm" : "text-muted-foreground hover:text-foreground")}>
                  {m === "chat" ? "Chat" : "Form"}
                </button>
              ))}
            </div>
          )}
          <button type="button" onClick={() => setGuideOpen((o) => !o)} aria-expanded={guideOpen} className="inline-flex h-9 items-center gap-1.5 rounded-xl border bg-card px-3 text-sm transition-quick hover:bg-muted" data-testid="field-guide-toggle">
            {guideOpen ? <PanelRightClose className="!h-4 !w-4" aria-hidden="true" /> : <PanelRightOpen className="!h-4 !w-4" aria-hidden="true" />} Field guide
          </button>
          {ready && <SessionContextChip userId={user.id} context={context} overrides={overrides} onOverride={(k, v) => setOverrides((o) => ({ ...o, [k]: v }))} />}
        </div>
      </div>
      {gates.length > 0 && <GateStrip gates={gates} ticks={run?.gate_ticks || []} onToggle={toggleGate} disabled={!run} />}
      <FormAlert testid="run-surface-error">{error}</FormAlert>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          {!entity.live ? <ComingSoon team={team} /> : !canRun ? (
            <ComingSoon team={team} text="Your role can read about this one but not run it. Ask an Owner if you need access." />
          ) : !slug ? <ComingSoon team={team} text={`The ${mode === "chat" ? "chat" : "form"} version is coming soon. Switch to the other version to run it now.`} /> : (
            <>
              <p className="mb-3 flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-sm md:hidden" data-testid="run-surface-mobile-note">
                <MonitorSmartphone className="!h-4 !w-4" aria-hidden="true" /> Best on a larger screen. It still works here.
              </p>
              {run ? (
                <div className="overflow-hidden rounded-2xl border bg-card" style={{ height: "max(760px, calc(100vh - 230px))" }}>
                  <MindPalEmbed elementId={`run-surface-${targetId}`} host={mode === "chat" ? "chatbot" : "workflow"} slug={slug} userId={user.id} context={context} />
                </div>
              ) : error ? null : <Skeleton className="h-[760px] rounded-2xl" />}
            </>
          )}
        </div>
        {guideOpen && <div className="w-[300px] shrink-0 max-lg:fixed max-lg:inset-y-0 max-lg:right-0 max-lg:z-40 max-lg:overflow-y-auto max-lg:bg-background max-lg:p-4 max-lg:shadow-2xl" data-testid="field-guide-drawer"><FieldGuide entity={entity} fields={guideFields}>{guideExtra}</FieldGuide></div>}
      </div>
    </section>
  );
};
